import React, { useEffect, useState } from "react";
import { HitApi } from "../../Api/ApiHit";
import { searchEvidence, searchTask, searchUser } from "../../constant/Constant";
import PageHeader from "../../PageHeader/PageHeader";
import { CardTask } from "../../components/Cards/CardTask";
import { getSelectedUnit } from "../../storage/Storage";
import TodaysTask from "./TaskTypes/TodaysTask";
import WeeklyTask from "./TaskTypes/WeeklyTask";
import MonthlyTask from "./TaskTypes/MonthlyTask";
import OneTimeTask from "./TaskTypes/OneTimeTask";
import SearchableSelectNonRedux from "../../components/AppSelect/SearchableSelectNonRedux";
import { User } from "lucide-react";

function AdminTaskDashbord() {
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUserObj, setSelectedUserObj] = useState(null);

  const [weekly, setWeekly] = useState(0);
  const [monthly, setMonthly] = useState(0);
  const [once, setOnce] = useState(0);
  const [todaysTask, setTodaysTask] = useState(0);
  const [missedTask, setMissedTask] = useState(0);
  const [completedTask, setCompletedTask] = useState(0);
  const [activeTab, setActiveTab] = useState("today");

  // Load initial users on component mount
  useEffect(() => {
    loadInitialUsers();
  }, []);

  // Fetch tasks when user is selected
  useEffect(() => {
    if (selectedUserId) {
      getTasksEvidence("today");
      getTask("weekly");
      getTask("monthly");
      getTask("once");
      getTasksEvidence("missed");
      getTasksEvidence("completed");
    }
  }, [selectedUserId]);

  const loadInitialUsers = () => {
    const json = {
      page: 1,
      limit: 20,
      search: {}
    };

    HitApi(json, searchUser)
      .then((res) => {
        console.log("Initial Users Load:", res);
        if ((res?.status === 200 || res?.statusCode === 200) && res?.data?.docs) {
          const options = res.data.docs.map(user => ({
            value: user._id,
            label: `${user.name || 'No Name'} ${user.email ? `(${user.email})` : ''} ${user.phone ? `- ${user.phone}` : ''}`.trim(),
            userData: user
          }));
          console.log("Initial User Options:", options);
          setUserOptions(options);
        }
      })
      .catch((error) => {
        console.error("Error loading initial users:", error);
      });
  };

  const handleUserSearch = (query) => {
    if (query.length >= 2) {
      const json = {
        page: 1,
        limit: 20,
        search: {
          $or: [
            { name: { $regex: query, $options: "i" } },
            { email: { $regex: query, $options: "i" } },
            { phone: { $regex: query, $options: "i" } }
          ]
        }
      };

      HitApi(json, searchUser)
        .then((res) => {
          console.log("Search User API Response:", res);
          if ((res?.status === 200 || res?.statusCode === 200) && res?.data?.docs) {
            const options = res.data.docs.map(user => ({
              value: user._id,
              label: `${user.name || 'No Name'} ${user.email ? `(${user.email})` : ''} ${user.phone ? `- ${user.phone}` : ''}`.trim(),
              userData: user
            }));
            console.log("User Options:", options);
            setUserOptions(options);
          } else {
            console.log("No users found or invalid response");
            setUserOptions([]);
          }
        })
        .catch((error) => {
          console.error("Error fetching users:", error);
          setUserOptions([]);
        });
    } else if (query === "") {
      // Load initial users when field is empty
      const json = {
        page: 1,
        limit: 20,
        search: {}
      };

      HitApi(json, searchUser)
        .then((res) => {
          console.log("Initial User Load API Response:", res);
          if ((res?.status === 200 || res?.statusCode === 200) && res?.data?.docs) {
            const options = res.data.docs.map(user => ({
              value: user._id,
              label: `${user.name || 'No Name'} ${user.email ? `(${user.email})` : ''} ${user.phone ? `- ${user.phone}` : ''}`.trim(),
              userData: user
            }));
            setUserOptions(options);
          }
        })
        .catch((error) => {
          console.error("Error fetching initial users:", error);
        });
    } else {
      setUserOptions([]);
    }
  };

  const handleUserSelect = (userId, userOption) => {
    setSelectedUserId(userId);
    setSelectedUserObj(userOption?.userData || null);
    // Reset counts
    setWeekly(0);
    setMonthly(0);
    setOnce(0);
    setTodaysTask(0);
    setMissedTask(0);
    setCompletedTask(0);
  };

  const getTasksEvidence = (type) => {
    if (!selectedUserId) return;

    let json = {};

    if (type === "today") {
      const today = new Date();
      const startTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0).getTime();
      const endTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).getTime();

      json = {
        page: 1,
        limit: 1,
        search: {
          assignedTo: { $in: [selectedUserId] },
          createdAt: {
            $gte: startTime,
            $lte: endTime,
          },
        },
      };

      HitApi(json, searchEvidence)
        .then((res) => {
          if (res?.statusCode === 200) {
            const total = res?.data?.docs?.length || 0;
            setTodaysTask(total);
          }
        })
        .catch((error) => {
          console.error("Error fetching today's tasks:", error);
        });
    }

    if (type === "missed") {
      json = {
        page: 1,
        limit: 1,
        search: {
          assignedTo: { $in: [selectedUserId] },
          "taskIntervals.status": "missed",
        },
      };

      HitApi(json, searchEvidence)
        .then((res) => {
          if (res?.status === 200) {
            const total = res?.doc?.totalDocs || res?.doc?.docs?.length || 0;
            setMissedTask(total);
          }
        })
        .catch((error) => {
          console.error("Error fetching missed tasks:", error);
        });
    }

    if (type === "completed") {
      json = {
        page: 1,
        limit: 1,
        search: {
          assignedTo: { $in: [selectedUserId] },
          "taskIntervals.status": "completed"
        },
      };

      HitApi(json, searchEvidence)
        .then((res) => {
          if (res?.statusCode === 200) {
            const total = res?.data?.totalDocs || res?.data?.docs?.length || 0;
            setCompletedTask(total);
          }
        })
        .catch((error) => {
          console.error("Error fetching completed tasks:", error);
        });
    }
  };

  const getTask = (taskFrequency) => {
    if (!selectedUserId) return;

    const json = {
      page: 1,
      limit: 1,
      search: {
        assignedTo: { $in: [selectedUserId] },
        taskFrequency: taskFrequency,
        unitIds: getSelectedUnit()?._id,
      },
    };

    HitApi(json, searchTask)
      .then((res) => {
        if (res?.data?.docs) {
          const total = res?.data?.totalDocs || 0;
          if (taskFrequency === "weekly") setWeekly(total);
          if (taskFrequency === "monthly") setMonthly(total);
          if (taskFrequency === "once") setOnce(total);
        }
      })
      .catch((error) => {
        console.error(`Error fetching ${taskFrequency} tasks:`, error);
      });
  };

  const tasks = [
    { title: "Today's Tasks", count: todaysTask, description: "Tasks due today", tab: "today" },
    { title: "Weekly Tasks", count: weekly, description: "Tasks this week", tab: "weekly" },
    { title: "Monthly Tasks", count: monthly, description: "Tasks this month", tab: "monthly" },
    { title: "One Time Tasks", count: once, description: "All one-time tasks", tab: "once" },
  ];

  const handleClick = (task) => {
    setActiveTab(task?.tab);
  };

  return (
    <div className="md:p-5">
      <PageHeader
        title="Task Dashboard"
        description="Manage functional tasks for all users"
      />

      {/* User Selector */}
      <div className="mt-6 max-w-md">
        <SearchableSelectNonRedux
          title="Select User"
          name="userId"
          options={userOptions}
          onSearch={handleUserSearch}
          onSelect={handleUserSelect}
          placeholder="Search by name, email, or phone..."
          value={selectedUserId}
          icon={<User size={20} />}
        />

        {selectedUserObj && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-gray-600">Selected:</span>
            <span className="text-sm font-medium text-gray-900">{selectedUserObj.name}</span>
            <button
              onClick={() => {
                setSelectedUserId("");
                setSelectedUserObj(null);
                loadInitialUsers(); // Reload initial users after clearing
                setWeekly(0);
                setMonthly(0);
                setOnce(0);
                setTodaysTask(0);
                setMissedTask(0);
                setCompletedTask(0);
              }}
              className="text-xs text-red-600 hover:text-red-700 underline"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Task Cards - Only show if user is selected */}
      {selectedUserId && (
        <>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {tasks.map((task, index) => (
              <CardTask
                key={index}
                task={task}
                onClick={() => handleClick(task)}
                isActive={activeTab === task.tab}
              />
            ))}
          </div>

          <div className="mt-10">
            {activeTab === "today" && <TodaysTask selectedUserId={selectedUserId} />}
            {activeTab === "weekly" && <WeeklyTask selectedUserId={selectedUserId} />}
            {activeTab === "monthly" && <MonthlyTask selectedUserId={selectedUserId} />}
            {activeTab === "once" && <OneTimeTask selectedUserId={selectedUserId} />}
          </div>
        </>
      )}

      {/* Empty state */}
      {!selectedUserId && (
        <div className="mt-10 text-center py-12 bg-gray-50 rounded-lg">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No user selected</h3>
          <p className="mt-1 text-sm text-gray-500">
            Search and select a user to view their tasks
          </p>
        </div>
      )}
    </div>
  );
}

export default AdminTaskDashbord;